import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { UserService } from "@/services/UserService";
import { UserSearchInput } from "@/components/ui/UserSearchInput";
import { User } from "@/common/model/User";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui";

interface DeviceOption {
  id: number;
  brand?: string;
  model?: string;
  os?: string;
  uniqueid?: string;
  createdate?: string;
}

interface KiteOption {
  id: number;
  kite?: string;
  kitemodelandsize?: string;
}

interface AssignSessionDialogProps {
  open: boolean;
  onClose: () => void;
  sessionId: number;
  currentRemark?: string;
  onSuccess: () => void;
}

export const AssignSessionDialog: React.FC<AssignSessionDialogProps> = ({
  open,
  onClose,
  sessionId,
  currentRemark,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [kites, setKites] = useState<KiteOption[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [selectedKiteId, setSelectedKiteId] = useState<string>("");
  const [remark, setRemark] = useState(currentRemark || "");
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedUser(null);
      setDevices([]);
      setKites([]);
      setSelectedDeviceId("");
      setSelectedKiteId("");
      setRemark(currentRemark || "");
      setReason("");
      setError(null);
    }
  }, [open, currentRemark]);

  const handleUserSelect = async (selectedUser: User) => {
    setSelectedUser(selectedUser);
    setSelectedDeviceId("");
    setSelectedKiteId("");
    setError(null);
    setIsLoading(true);

    try {
      const [devicesData, kitesData] = await Promise.all([
        UserService.getUserDevices(selectedUser.id, user!.accesstoken),
        UserService.getUserKites(selectedUser.id, user!.accesstoken),
      ]);
      setDevices(devicesData || []);
      setKites(kitesData || []);
    } catch (err) {
      console.error("Error loading user data:", err);
      setError("Failed to load user devices and kites");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedUser || !selectedDeviceId) return;

    setIsSaving(true);
    setError(null);

    try {
      await UserService.assignSession(
        sessionId,
        {
          targetUserId: selectedUser.id,
          deviceId: Number(selectedDeviceId),
          userKiteId: selectedKiteId ? Number(selectedKiteId) : undefined,
          remark: remark || undefined,
          reason: reason || undefined,
        },
        user!.accesstoken
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error assigning session:", err);
      setError(
        err.response?.data?.error ||
          "Failed to assign session. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const formatDeviceLabel = (device: DeviceOption) => {
    const parts = [device.brand, device.model].filter(Boolean);
    const label = parts.length > 0 ? parts.join(" ") : "Unknown device";
    const date = device.createdate
      ? new Date(device.createdate).toLocaleDateString()
      : "";
    return `${label}${date ? ` (${date})` : ""} - ID: ${device.id}`;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Session to User</DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* User Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target User
            </label>
            {selectedUser ? (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <img
                    src={
                      selectedUser.profilepicid
                        ? UserService.getProfileImageUrl(
                            selectedUser.profilepicid
                          )
                        : "/default-profilepic.png"
                    }
                    alt="Profile"
                    className="h-8 w-8 rounded-full object-cover"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedUser.name}
                    </span>
                    <span className="text-sm text-gray-500 ml-2">
                      ({selectedUser.username})
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedUser(null);
                    setDevices([]);
                    setKites([]);
                    setSelectedDeviceId("");
                    setSelectedKiteId("");
                  }}
                  className="text-sm text-cyan-600 hover:text-cyan-700"
                >
                  Change
                </button>
              </div>
            ) : (
              <UserSearchInput
                theme="light"
                onUserSelect={handleUserSelect}
                adminMode
              />
            )}
          </div>

          {/* Device Dropdown */}
          {selectedUser && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Device *
              </label>
              {isLoading ? (
                <p className="text-sm text-gray-500">Loading devices...</p>
              ) : devices.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No devices found for this user
                </p>
              ) : (
                <select
                  value={selectedDeviceId}
                  onChange={(e) => setSelectedDeviceId(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  <option value="">Select a device...</option>
                  {devices.map((device) => (
                    <option key={device.id} value={device.id}>
                      {formatDeviceLabel(device)}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Kite Dropdown */}
          {selectedUser && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kite (optional)
              </label>
              {isLoading ? (
                <p className="text-sm text-gray-500">Loading kites...</p>
              ) : kites.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No kites found for this user
                </p>
              ) : (
                <select
                  value={selectedKiteId}
                  onChange={(e) => setSelectedKiteId(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  <option value="">None</option>
                  {kites.map((kite) => (
                    <option key={kite.id} value={kite.id}>
                      {kite.kite || kite.kitemodelandsize || `Kite #${kite.id}`}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Remark */}
          {selectedUser && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Session Notes
              </label>
              <textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                rows={2}
                placeholder="Optional session notes..."
              />
            </div>
          )}

          {/* Reason */}
          {selectedUser && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for reassignment
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="e.g. User used wrong device"
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedUser || !selectedDeviceId || isSaving}
          >
            {isSaving ? "Assigning..." : "Assign Session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
